import { z } from 'zod';
import { IsoDate, Timestamp, Uuid } from './envelope';
import { ChartPayload, MetricValue } from './metric';
import { EntityRef } from './read-models';

/**
 * Flow read-models — the Value Stream & Flow Analytics projections.
 *
 * These exist because `fact.work_item` records four timestamps and nothing
 * about movement. Every type below is derived from `fact.work_item_transition`
 * (migration 0076), and every one of them can honestly return nothing: a value
 * stream whose tracker has never sent a changelog has no flow time, and the
 * contract's job is to make that state expressible rather than to force a
 * number into it.
 *
 * Two rules are carried in the shapes themselves rather than left to the
 * screens:
 *
 *  - **Unclassified is a category.** `FlowDistribution` has an `unclassified`
 *    share beside the four Flow Framework ones, and the four are shares of
 *    *all* delivered items rather than of the classified subset. A stream where
 *    a third of the work carries no type says so; it does not report "62%
 *    features" over a denominator it quietly shrank.
 *  - **Coverage travels with the measure.** Anything derived from transitions
 *    carries `itemsWithHistory` and `itemsTotal`, so a reader can see that a
 *    flow efficiency of 24% was computed over eleven of a stream's four hundred
 *    items before they quote it.
 */

// ─────────────────────────────────────────────────────────────────────────────
// The four flow states
// ─────────────────────────────────────────────────────────────────────────────

/**
 * `New` — committed to, not started. Queue time, and not counted as load.
 * `Active` — being worked on. The only state that counts as active time.
 * `Waiting` — started and blocked: review, dependency, environment, decision.
 * `Done` — delivered.
 */
export const FlowState = z.enum(['New', 'Active', 'Waiting', 'Done']);
export type FlowState = z.infer<typeof FlowState>;

/**
 * The Flow Framework's four categories.
 *
 * `Risk` is security, compliance and governance work. Aurora's tracker calls
 * that category Compliance; `ref.work_item_type` records the mapping so the
 * judgement is data rather than a CASE expression in a route.
 */
export const FlowCategory = z.enum(['Feature', 'Defect', 'Debt', 'Risk']);
export type FlowCategory = z.infer<typeof FlowCategory>;

// ─────────────────────────────────────────────────────────────────────────────
// The value stream model — the stages an owner defines
// ─────────────────────────────────────────────────────────────────────────────

export const ValueStreamStateRow = z.object({
  id: Uuid,
  name: z.string().min(1),
  position: z.number().int().positive(),
  flowState: FlowState,
  /** Extra tracker state names collected into this stage. Usually empty. */
  sourceStates: z.array(z.string()),
  description: z.string().nullable(),
  /**
   * Items whose latest transition resolves onto this stage, right now.
   *
   * Null — not zero — when the stream has no transition history at all. Zero
   * means "nothing is here"; null means "nobody can say".
   */
  itemsHere: z.number().int().nonnegative().nullable(),
  /** Mean days an item spent in this stage, over items that have left it. */
  averageDaysHere: z.number().nonnegative().nullable(),
  /** Share of total elapsed time across the stream that this stage holds. */
  shareOfElapsed: z.number().min(0).max(1).nullable(),
  /**
   * The items that have been sitting here longest, worst first.
   *
   * A stage average tells a reader that Deploy holds work for six days. It does
   * not tell them that one release has been stuck there for eleven weeks, which
   * is the thing somebody can act on this afternoon. Capped at five: this is a
   * way in to the item, not an inventory.
   */
  oldestItems: z.array(
    z.object({ workItem: EntityRef, daysHere: z.number().nonnegative().nullable() }),
  ),
});
export type ValueStreamStateRow = z.infer<typeof ValueStreamStateRow>;

/** What a caller may send to redefine a stream's stages. Order is the array's. */
export const ValueStreamStateInput = z.object({
  name: z.string().min(1).max(60),
  flowState: FlowState,
  sourceStates: z.array(z.string().min(1).max(60)).max(12).default([]),
  description: z.string().max(400).nullable().default(null),
});
export type ValueStreamStateInput = z.infer<typeof ValueStreamStateInput>;

// ─────────────────────────────────────────────────────────────────────────────
// The five Flow Framework metrics
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Flow Distribution — the share of delivered work by category.
 *
 * Every share is over `delivered`, including `unclassified`. The five numbers
 * therefore sum to one, and a reader can see the whole denominator rather than
 * an implied one.
 */
export const FlowDistribution = z.object({
  delivered: z.number().int().nonnegative(),
  categories: z.array(
    z.object({
      category: FlowCategory,
      items: z.number().int().nonnegative(),
      share: z.number().min(0).max(1),
    }),
  ),
  unclassified: z.object({
    items: z.number().int().nonnegative(),
    share: z.number().min(0).max(1),
  }),
});
export type FlowDistribution = z.infer<typeof FlowDistribution>;

export const FlowStreamRow = z.object({
  valueStream: EntityRef,
  productCount: z.number().int().nonnegative(),

  /** MET-075. Null where no delivered item in the window carries history. */
  flowTimeDays: z.number().nonnegative().nullable(),
  /** MET-076, 0–1. Null for the same reason. */
  flowEfficiency: z.number().min(0).max(1).nullable(),
  /** MET-077. Items delivered in the window. Zero is a real answer here. */
  flowVelocity: z.number().int().nonnegative(),
  /** MET-078. Items currently Active or Waiting. Null without history. */
  flowLoad: z.number().int().nonnegative().nullable(),
  /** MET-079. Always present; `delivered` may be zero. */
  flowDistribution: FlowDistribution,

  /** Mean days a delivered item spent in states mapped to Waiting. */
  waitDays: z.number().nonnegative().nullable(),

  /**
   * How much of the stream the transition-derived figures actually cover.
   *
   * `itemsWithHistory` of `itemsDelivered`. A figure over three of two hundred
   * items is not wrong, but quoting it without this is.
   */
  itemsDelivered: z.number().int().nonnegative(),
  itemsWithHistory: z.number().int().nonnegative(),

  /**
   * Said in the reader's words when a figure could not be produced. Null when
   * everything on the row is available.
   */
  unavailableReason: z.string().nullable(),
});
export type FlowStreamRow = z.infer<typeof FlowStreamRow>;

/**
 * A stage that work is piling up in.
 *
 * Distinct from `DeliveryOverview.bottlenecks`, which is an *architectural*
 * finding — a product resting on an application in poor technical health. This
 * one is about movement: the stage holding the largest share of elapsed time,
 * or the largest queue.
 */
export const FlowConstraint = z.object({
  valueStream: EntityRef,
  stateName: z.string(),
  flowState: FlowState,
  itemsHere: z.number().int().nonnegative(),
  averageDaysHere: z.number().nonnegative().nullable(),
  shareOfElapsed: z.number().min(0).max(1).nullable(),
  detail: z.string(),
});
export type FlowConstraint = z.infer<typeof FlowConstraint>;

export const FlowOverview = z.object({
  /** MET-075…079 at estate level, for the window in `meta`. */
  kpis: z.array(MetricValue),
  streams: z.array(FlowStreamRow),
  /** Flow velocity by month, one series per stream with any delivery. */
  velocityTrend: ChartPayload,
  constraints: z.array(FlowConstraint),
  /**
   * Estate-level coverage. `streamsWithHistory` of `streams`, so the page can
   * say plainly how much of the estate these figures describe.
   */
  coverage: z.object({
    streams: z.number().int().nonnegative(),
    streamsWithHistory: z.number().int().nonnegative(),
    itemsDelivered: z.number().int().nonnegative(),
    itemsWithHistory: z.number().int().nonnegative(),
  }),
  asOf: IsoDate,
});
export type FlowOverview = z.infer<typeof FlowOverview>;

// ─────────────────────────────────────────────────────────────────────────────
// One stream, as a map
// ─────────────────────────────────────────────────────────────────────────────

export const ValueStreamMapView = z.object({
  valueStream: EntityRef,
  owner: z.string().nullable(),
  purpose: z.string().nullable(),
  states: z.array(ValueStreamStateRow),
  products: z.array(EntityRef),
  metrics: FlowStreamRow,
  /**
   * Tracker states seen in this stream's transitions that no stage collects.
   *
   * Reported rather than absorbed. Work sitting in a state the model does not
   * know about is invisible to every figure on the map, and a map that is
   * quietly missing a stage is worse than one that says which stage is missing.
   */
  unmodelledStates: z.array(z.object({ state: z.string(), items: z.number().int().positive() })),
  canEdit: z.boolean(),
  asOf: IsoDate,
});
export type ValueStreamMapView = z.infer<typeof ValueStreamMapView>;

// ─────────────────────────────────────────────────────────────────────────────
// One work item's history
// ─────────────────────────────────────────────────────────────────────────────

export const WorkItemTransitionRow = z.object({
  id: Uuid,
  fromState: z.string().nullable(),
  toState: z.string(),
  flowState: FlowState,
  enteredAt: Timestamp,
  /** Days spent in `toState` before the next transition. Null while current. */
  daysInState: z.number().nonnegative().nullable(),
});
export type WorkItemTransitionRow = z.infer<typeof WorkItemTransitionRow>;

export const WorkItemHistoryView = z.object({
  workItem: EntityRef,
  product: EntityRef.nullable(),
  valueStream: EntityRef.nullable(),
  /** The tracker's type, and the category it rolls up to. Null when unclassified. */
  workItemType: z.string().nullable(),
  flowCategory: FlowCategory.nullable(),
  statusCode: z.string().nullable(),
  createdOn: Timestamp.nullable(),
  startedAt: Timestamp.nullable(),
  completedAt: Timestamp.nullable(),
  transitions: z.array(WorkItemTransitionRow),
  flowTimeDays: z.number().nonnegative().nullable(),
  activeDays: z.number().nonnegative().nullable(),
  waitDays: z.number().nonnegative().nullable(),
  flowEfficiency: z.number().min(0).max(1).nullable(),
  /** The item this one rolls up into, where the source stated one. */
  parent: EntityRef.nullable(),
  children: z.array(EntityRef),
  /**
   * Which tool holds this item, and where to open it.
   *
   * The flow history is a reconstruction: "this item waited eleven days in
   * review". The reader's next question is always whether that is right, and
   * the only way to answer it is to look at the ticket. Both null for an item
   * that arrived in a spreadsheet — a workbook row has no page to open, and a
   * link that went nowhere would be worse than none.
   */
  sourceSystem: z.string().nullable(),
  sourceUrl: z.string().url().nullable(),
  unavailableReason: z.string().nullable(),
});
export type WorkItemHistoryView = z.infer<typeof WorkItemHistoryView>;

// ─────────────────────────────────────────────────────────────────────────────
// Product work mix and business results — the Product 360 risks tab
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A product's delivered work by category, and the business results beside it.
 *
 * The four categories Planview calls business results are value, cost, quality
 * and delivery. The fifth thing that framework asks for — employee experience —
 * is recorded nowhere in this model, so it is named and reported as absent
 * rather than substituted with a proxy such as attrition or capacity, which
 * would be a different claim wearing its label.
 */
export const ProductWorkMixView = z.object({
  distribution: FlowDistribution,
  /** Roll-up across the work hierarchy, and how deep that hierarchy goes. */
  rollUp: z.object({
    /** 1 when no parent-child link is recorded, which is not the same as none existing. */
    depth: z.number().int().positive(),
    itemsAtDepth: z.array(z.object({ depth: z.number().int().positive(), items: z.number().int().nonnegative() })),
    completed: z.number().int().nonnegative(),
    total: z.number().int().nonnegative(),
    /** Said plainly when the depth is one because nothing was ingested. */
    note: z.string(),
  }),
  businessResults: z.array(
    z.object({
      result: z.enum(['Value', 'Cost', 'Quality', 'Delivery', 'EmployeeExperience']),
      label: z.string(),
      measure: MetricValue,
    }),
  ),
  /** The value stream this product's mix must reconcile with. */
  valueStream: EntityRef.nullable(),
  asOf: IsoDate,
});
export type ProductWorkMixView = z.infer<typeof ProductWorkMixView>;
